// Global keyboard shortcuts for the shell ("R" record, ⌘K / Ctrl+K jump).
//
// A single-letter shortcut must never steal a key someone is typing, so
// it is ignored when:
//   - any modifier is held (Ctrl/⌘/Alt; Shift too for letters),
//   - the key comes from a text field, select or contenteditable (the chat
//     composer), from a remote desktop / terminal (canvas, xterm,
//     role=application), or from a widget that uses letters itself
//     (listbox / menu typeahead, grid, tree, combobox, a dialog),
//   - something already handled it (defaultPrevented),
//   - a dialog or sheet is open, or a menu / dropdown is expanded.

import { useEffect, useRef } from "react";

export const IS_MAC =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** "⌘K" on a Mac, "Ctrl K" elsewhere. */
export const JUMP_HINT = IS_MAC ? "⌘K" : "Ctrl K";

function takesRawKeys(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return !!target.closest(
    "canvas, .xterm, [role='application'], [contenteditable='true'], " +
      "[role='listbox'], [role='option'], [role='menu'], [role='menuitem'], [role='combobox'], " +
      "[role='dialog'], [role='grid'], [role='tree']",
  );
}

// A modal is open, or a control says its menu / listbox / popover is open
// (Dropdown, Select, OverflowMenu, the bell): a letter there is typeahead
// or belongs to the popover, not a shortcut. Not
// "any [role=listbox] on screen" — the Projects explorer and the model
// picker are listboxes that are always there.
function overlayOpen(): boolean {
  return !!document.querySelector(
    "[role='dialog'][aria-modal='true'], " +
      ["listbox", "menu", "true", "dialog"].map((p) => `[aria-expanded='true'][aria-haspopup='${p}']`).join(", "),
  );
}

/** Run `action` on a bare letter key, with the guards above. */
export function useShortcut(key: string, action: () => void, enabled = true) {
  const ref = useRef(action);
  ref.current = action;
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      if (e.defaultPrevented || e.repeat || e.isComposing) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.key.toLowerCase() !== key) return;
      if (takesRawKeys(e.target) || overlayOpen()) return;
      e.preventDefault();
      ref.current();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [key, enabled]);
}

/** ⌘K / Ctrl+K, which works from anywhere, text fields included. */
export function useJumpShortcut(action: () => void) {
  const ref = useRef(action);
  ref.current = action;
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.defaultPrevented || e.repeat) return;
      if (!(IS_MAC ? e.metaKey : e.ctrlKey) || e.altKey || e.shiftKey) return;
      if (e.key.toLowerCase() !== "k") return;
      // A remote desktop keeps its own Ctrl+K.
      if (e.target instanceof HTMLElement && e.target.closest("canvas, .xterm")) return;
      e.preventDefault();
      ref.current();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
